import { useEffect, useState } from "react";
import { callFunction } from "../api/callFunction";
import { showToast } from "../utils/showToast";

// Platform-wide switches in clientConfig/features, read and written through
// the adminGet/adminUpdatePlatformFeatures callables (super admins only; the
// rules seal client writes). Each entry names a flag the backend declares in
// DEFAULT_FEATURES; an unknown key is dropped server-side, so keep them in sync.
const FEATURES = [
  {
    key: "attendanceQuestionEnabled",
    label: "Ask guests if they can make it",
    detail:
      "The day-before reminder becomes a question: \"Can you make it? Reply YES or NO.\" Answers show on the guest list, and the host gets a text with the count. Off, guests get the plain reminder.",
  },
  {
    key: "attendanceNoFreesSpot",
    label: "A no gives the spot back",
    detail:
      "When a guest answers no, take them off the guest list so someone else can have the spot. Only for free spots. A guest who paid keeps theirs (no refunds) until a host removes them. Off, a no is only recorded.",
  },
  {
    key: "postEventQuestionEnabled",
    label: "Ask guests how the event was",
    detail:
      "The day after an event, guests who came get a text asking them to rate it from 1 to 5, with a link to connect with people they met. Hosts see the average and comments without names under Reviews once 3 or more have rated. It replaces the old after-event text and the Gist AI feedback card. Off, nobody new is asked and those go out as before.",
  },
];

const Switch = ({ enabled, disabled, onToggle, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    aria-label={label}
    onClick={onToggle}
    aria-busy={disabled}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
      enabled ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"
    } ${disabled ? "opacity-60" : ""}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
        enabled ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

export default function PlatformFeatures() {
  // null until the server has told us where each switch stands. It stays null
  // when that read fails: an unread switch drawn as "off" would be a guess, and
  // the one click it offers would turn the thing ON.
  const [features, setFeatures] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    callFunction("adminGetPlatformFeatures", {})
      .then((res) => {
        if (!cancelled) setFeatures(res?.features || {});
      })
      .catch((err) => {
        console.error("[PlatformFeatures] adminGetPlatformFeatures error:", err);
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = async (key) => {
    if (busyKey || !features) return;
    const next = !features[key];
    setBusyKey(key);
    setFeatures((prev) => ({ ...prev, [key]: next }));
    try {
      const res = await callFunction("adminUpdatePlatformFeatures", { [key]: next });
      if (res?.features) setFeatures(res.features);
      showToast(next ? "Turned on" : "Turned off");
    } catch (err) {
      console.error("[PlatformFeatures] adminUpdatePlatformFeatures error:", err);
      setFeatures((prev) => ({ ...prev, [key]: !next }));
      showToast("Could not save that setting", { type: "error" });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="border-b border-gray-900/10 dark:border-white/10 pb-6 mt-4">
      <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Platform features</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Switches that apply to every event and every member. They take effect without a deploy.
      </p>
      {loadFailed && (
        <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
          Could not load these switches, so they are locked. Reload the page to try again.
        </p>
      )}
      <ul className="mt-4 space-y-4">
        {FEATURES.map((f) => (
          <li key={f.key} className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{f.label}</p>
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{f.detail}</p>
            </div>
            <Switch
              enabled={!!features?.[f.key]}
              disabled={features === null || busyKey === f.key}
              onToggle={() => toggle(f.key)}
              label={f.label}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
